import { useMemo } from "react";
import { durationLabel } from "../../lib/rhythm/rhythmPathPlan";
import { musicNoteGlyph } from "../../lib/rhythm/musicGlyphs";
import type { ExpectedHit } from "../../lib/rhythm/rhythmEngine";

export interface RhythmDurationLegendProps {
  expected: ExpectedHit[];
  /** Duración activa (highlight). */
  activeBeats?: number | null;
  isRestActive?: boolean;
  className?: string;
}

type LegendEntry = {
  beats: number;
  isRest: boolean;
  label: string;
  times: string;
};

/**
 * Recordatorio de las figuras (duraciones) presentes en el ejercicio actual.
 */
export function RhythmDurationLegend({
  expected,
  activeBeats = null,
  isRestActive = false,
  className = "",
}: RhythmDurationLegendProps) {
  const entries = useMemo(() => {
    const map = new Map<string, LegendEntry>();
    for (const h of expected) {
      const key = `${h.isRest ? "r" : "n"}:${h.durationBeats}`;
      if (map.has(key)) continue;
      map.set(key, {
        beats: h.durationBeats,
        isRest: h.isRest,
        label: h.isRest ? "Silencio" : durationLabel(h.durationBeats),
        times: `${h.durationBeats} t.`,
      });
    }
    return [...map.values()].sort((a, b) => b.beats - a.beats || Number(a.isRest) - Number(b.isRest));
  }, [expected]);

  if (!entries.length) return null;

  return (
    <aside
      className={`rounded-xl border border-white/10 bg-black/30 px-2.5 py-2.5 ${className}`}
      aria-label="Figuras de este ejercicio"
    >
      <p className="mb-2 text-[9px] uppercase tracking-[0.14em] text-white/35">
        Figuras
      </p>
      <ul className="flex flex-col gap-1.5">
        {entries.map((e) => {
          const on =
            activeBeats != null &&
            Math.abs(activeBeats - e.beats) < 0.05 &&
            Boolean(isRestActive) === e.isRest;
          return (
            <li
              key={`${e.isRest}-${e.beats}`}
              className={`flex items-center gap-2 rounded-lg px-1.5 py-1 transition-colors ${
                on ? "bg-[#99f6e4]/12" : ""
              }`}
            >
              <span
                className={`font-music text-2xl leading-none ${
                  e.isRest
                    ? on
                      ? "text-amber-200"
                      : "text-amber-200/70"
                    : on
                      ? "text-[#99f6e4]"
                      : "text-white/80"
                }`}
                aria-hidden
              >
                {musicNoteGlyph(e.beats, e.isRest)}
              </span>
              <div className="min-w-0 leading-tight">
                <p
                  className={`truncate text-[11px] ${
                    on ? "text-white/90" : "text-white/55"
                  }`}
                >
                  {e.label}
                </p>
                <p className="text-[10px] tabular-nums text-white/35">{e.times}</p>
              </div>
            </li>
          );
        })}
      </ul>
    </aside>
  );
}
